import { array } from 'fp-ts';
import { useCallback, useMemo, useRef, useState } from 'react';
import { NonEmptyArray } from 'fp-ts/NonEmptyArray';
import { useTheme } from 'styled-components';
import { WithEnterpriseId, WithServiceId } from '@mews-commander/types';
import { Empty, OverlayedLoader } from '@mews-commander/ui';
import { ElementOrElementGetter } from '@optimus-web/core';
import { Matrix } from '@mews/b2b-ui';
import { useAppClockService } from '@mews/framework';
import { getEnterpriseTimeZoneIdentifier } from '@mews-commander/legacy-entities/src/ducks/enterprises';
import { useCommanderSelector } from '@mews-commander/utils';
import { FilterFormValues } from '../../schemas';
import { SellLimitMatrixRowData } from '../../types';
import { getSellLimitsMatrixMaxHeight } from '../../constants';
import { useSellLimitsMatrixData } from '../../queries';
import { DailyLimitsPopover } from '../dailyLimits';
import { CellClickHandler, CellClickHandlerCellData, createSellLimitsMatrixMapping } from './mapping';

interface SellLimitsMatrixContainerProps extends WithServiceId, WithEnterpriseId {
    filterValues: FilterFormValues;
}

export const SellLimitsMatrixContainer = ({
    enterpriseId,
    filterValues,
    serviceId,
}: SellLimitsMatrixContainerProps) => {
    const { data, isFetching, isSuccess } = useSellLimitsMatrixData({ filterValues, serviceId });
    const anchorElementRef = useRef<ElementOrElementGetter | undefined>();
    const [cellData, setCellData] = useState<CellClickHandlerCellData | undefined>();

    const handleCellClick = useCallback<CellClickHandler>(
        (e, value) => {
            if (e.currentTarget === anchorElementRef.current) return;

            e.nativeEvent.stopImmediatePropagation();
            anchorElementRef.current = e.currentTarget;
            setCellData(value);
        },
        [setCellData]
    );
    const handlePopoverClose = () => {
        anchorElementRef.current = undefined;
        setCellData(undefined);
    };

    return (
        <>
            <OverlayedLoader show={isFetching} />
            {isSuccess && array.isNonEmpty(data) ? (
                <SellLimitMatrix
                    data={data}
                    selectedCellData={cellData}
                    onCellClick={handleCellClick}
                    enterpriseId={enterpriseId}
                />
            ) : (
                <Empty />
            )}
            {cellData ? (
                <DailyLimitsPopover
                    serviceId={serviceId}
                    enterpriseId={enterpriseId}
                    filterValues={filterValues}
                    resourceCategoryId={cellData.resourceCategoryId}
                    timeUnit={cellData.timeUnit}
                    anchorElement={anchorElementRef.current}
                    closePopover={handlePopoverClose}
                />
            ) : null}
        </>
    );
};

interface SellLimitMatrixProps extends WithEnterpriseId {
    data: NonEmptyArray<SellLimitMatrixRowData>;
    onCellClick: CellClickHandler;
    selectedCellData?: CellClickHandlerCellData;
}

const SellLimitMatrix = ({ data, onCellClick, enterpriseId, selectedCellData }: SellLimitMatrixProps) => {
    const theme = useTheme();
    const timezone = useCommanderSelector((state) => getEnterpriseTimeZoneIdentifier(state, { enterpriseId }));
    const appClockService = useAppClockService();

    const mapping = useMemo(
        () =>
            createSellLimitsMatrixMapping({ appClockService, rowData: data, onCellClick, timezone, selectedCellData }),
        [appClockService, data, onCellClick, timezone, selectedCellData]
    );

    return (
        <Matrix
            bordered
            mapping={mapping}
            source={data}
            maxHeight={getSellLimitsMatrixMaxHeight(theme)}
            fixedFirstColumn
            fixedHeader
        />
    );
};
